import { PaymentMethod } from '@stripe/stripe-js';

/**
 * Payment methods the payments SDK supports directly.
 */
export const SupportedPaymentMethods = {
  CARD: 'card',
  PAYPAL: 'pay_pal',
  BANK: 'ebanking',
  OXXO: 'oxxo',
  AFFIRM: 'affirm',
  APPLEPAY: 'apple_pay',
  GOOGLEPAY: 'google_pay',
} as const;

/**
 * Base payment methods that the SDK supports directly.
 * These are the core payment methods managed by the Payment SDK.
 */
export type BasePaymentMethodsTypes =
  | 'card'
  | 'pay_pal'
  | 'ebanking'
  | 'oxxo'
  | 'sepa_debit'
  | 'ideal'
  | 'affirm'
  | 'apple_pay'
  | 'google_pay';

/**
 * Payment methods derived from Stripe's PaymentMethod type.
 * These represent additional payment methods available through Stripe's
 * Payment Element that are not part of the base SDK payment methods.
 */
export type StripePaymentMethodsTypes = Exclude<
  keyof PaymentMethod,
  'type' | 'livemode' | 'customer' | 'allow_redisplay' | 'object' | 'id'
>;

/**
 * Union of all payment method types - both base SDK methods and Stripe-specific methods.
 * Use this when a value could be any supported payment method.
 */
export type AllPaymentMethodsTypes =
  | BasePaymentMethodsTypes
  | StripePaymentMethodsTypes;

/**
 * @deprecated Use BasePaymentMethodsTypes instead.
 * Kept for backwards compatibility with external consumers.
 */
export type PaymentMethodsTypes = BasePaymentMethodsTypes;

/**
 * Payment processors the payments SDK supports directly.
 */
export const SupportedProcessors = {
  STRIPE: 'stripe',
  ADYEN: 'adyen',
  PAYPAL: 'paypal',
  DLOCAL: 'dlocal',
  TOKENEX: 'tokenex',
  PAYU: 'payu',
} as const;

export type ProcessorsType =
  (typeof SupportedProcessors)[keyof typeof SupportedProcessors];

export const Locale = [
  'ach_UG',
  'ca_ES',
  'de_DE',
  'en_US',
  'es_MX',
  'fr_FR',
  'id_ID',
  'it_IT',
  'pt_BR',
  'th_TH',
] as const;

export const zeroDecimalCurrencies = [
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
];

export const threeDecimalCurrencies = [
  'bhd',
  'iqd',
  'jod',
  'kwd',
  'lyd',
  'omr',
  'tnd',
];

export const BillingRequiredValues = {
  MANDATORY: 'mandatory',
  OPTIONAL: 'optional',
};

export const GatewayModeValues = {
  LIVE: 'live',
  TEST: 'test',
};

export const CaptureMode = {
  CHARGE: 'auto',
  AUTH: 'manual',
} as const;

/**
 * @deprecated
 */
export const CloudbedsDomains = {
  prod: 'hotels.cloudbeds.com',
  prod2: 'us2.cloudbeds.com',
  stage: 'hotels.cloudbeds-stage.com',
  stage2: 'us2.cloudbeds-stage.com',
  dev: 'us2.cloudbeds-stage.com',
} as const;

export const PAYMENT_METHOD_HASH_TYPE_STRIPE = 'stripe_payment_method';
export const PAYMENT_METHOD_HASH_TYPE_PAYPAL = 'paypal';
export const PAYMENT_METHOD_HASH_TYPE_TOKENEX = 'card_tokenex';
export const PAYMENT_METHOD_HASH_TYPE_OXXO = 'oxxo';
export const PAYMENT_METHOD_HASH_TYPE_AFFIRM = 'affirm';
export const PAYMENT_METHOD_HASH_TYPE_STRIPE_PAYMENT_ELEMENT =
  'stripe_confirmation_token';

export const HTTP_METHOD_GET = 'GET';
export const HTTP_METHOD_POST = 'POST';

/**
 * List of all available next actions
 */
const NEXT_ACTIONS = {
  DONE: 'done',
  REDIRECT: 'redirect',
  STRIPE_CONFIRM: 'stripe_confirmation_details',
  STRIPE_PAYMENT_ELEMENT: 'stripe_sdk', // strange name from payment element but it comes from backend
  PAYPAL_ORDER: 'paypal_details',
  OXXO_VOUCHER: 'oxxo_voucher',
} as const;

/**
 * Descending order of preference for default network to use with co-badged
 * cards. Order selected based on popularity and international spread.
 */
export const preferredNetwork = [
  'visa',
  'mastercard',
  'amex',
  'discover',
  'diners',
  'unionpay',
  'jcb',
  'interac',
] as const;

export const NEXT_ACTION_TYPE_DONE = NEXT_ACTIONS.DONE;
export const NEXT_ACTION_TYPE_REDIRECT = NEXT_ACTIONS.REDIRECT;
export const NEXT_ACTION_TYPE_STRIPE_CONFIRM = NEXT_ACTIONS.STRIPE_CONFIRM;
export const NEXT_ACTION_TYPE_STRIPE_PAYMENT_ELEMENT =
  NEXT_ACTIONS.STRIPE_PAYMENT_ELEMENT;
export const NEXT_ACTION_TYPE_PAYPAL_ORDER = NEXT_ACTIONS.PAYPAL_ORDER;
export const NEXT_ACTION_TYPE_OXXO_VOUCHER = NEXT_ACTIONS.OXXO_VOUCHER;

export type NextActionType = (typeof NEXT_ACTIONS)[keyof typeof NEXT_ACTIONS];
